import React, {useEffect, useState} from 'react';
import { useRequest } from 'ahooks';
import { Bubble, Button, Form, Input, Modal, Select, notification } from 'tea-component';
import { getLocale, shallowEqual, useSelector } from 'umi';
import { publishDashboard } from '@dbl/services/dashboard';
import { useFolderHooks, useListUserLevel, useMenuHooks, useNameHooks, useNull, useSubFolderHooks } from './_hooks';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { useGetMenuList } from './useGetMenuList';
import Abnormal from '@/pages/home/components/Abnormal';

import './index.scss';

const PublishModal = (props: any) => {
  const { closeFunc, publishId, dashboardName, refreshTableListData } = props;
  const [hasClickedLevel, setHasClickedLevel] = useState(false);
  const { formatMessage, commonFormatMessage } = useDblIntl("dashboard_list");
  const {
    menuList,
    folderList,
    subFolderList,
    levelList,
    menuLoading,
    menuError,
    levelLoading,
    levelError,
  } = useGetMenuList();
  const locale = getLocale();
  const { gameCode, currentGame } = useSelector((state: any) => ({
    gameCode: state?.datalab?.gameCode,
    currentGame: state?.datalab?.currentGame
  }), shallowEqual);
  // menu相关
  const {
    selectMenu,
    setSelectMenu,
    menuErr,
    formatMenuList,
    checkMenu
  } = useMenuHooks({ currentGame, menuList, locale, formatMessage });

  // folder相关
  const {
    selectFolder,
    setSelectFolder,
    folderErr,
    formatFolderList,
    checkFolder
  } = useFolderHooks({ folderList, selectMenu, locale, formatMessage });

  // subfolder相关
  const {
    selectSubFolder,
    setSelectSubFolder,
    subFolderErr,
    formatSubFolderList,
  } = useSubFolderHooks({ subFolderList, selectFolder, locale, formatMessage });
  // userLevel
  const {
    userLevelList,
    selectedLevel,
    setSelectedLevel,
    userLevelErr,
    checkUserLevel
  } = useListUserLevel({ formatMessage, levelList });

  // 名字相关
  const {
    nameCN,
    setNameCN,
    nameEN,
    setNameEN,
    nameENErr,
    nameCNErr,
    checkNameEN,
    checkNameCN,
    setNameENErr
  } = useNameHooks(formatMessage, dashboardName, 'dashboard');

  const { isNull = false } = useNull(selectedLevel, nameEN, selectMenu, selectFolder);
  const isError = nameCNErr || nameENErr || menuErr || folderErr || userLevelErr;

  /** 发布报表 */
  const { run: publishDashboardItem } = useRequest((params) => publishDashboard(params), {
    manual: true,
    onSuccess: (data: any, params: any) => {
      // 发布成功通知
      notification.success({
        description: commonFormatMessage("publish_successfully", {name: params[0].name_en}),
        duration: 1000
      });
      refreshTableListData?.();
      closeFunc();
    },
    onError: (err: any) => {
      if (err?.code === 51000) {
        setNameENErr(formatMessage({ id: 'dbl_menu_dashboard_re_enter' }));
      } else {
        // 发布失败通知
        notification.error({
          description: commonFormatMessage('publish_failed', {code: err?.code || ''}),
          duration: 1000
        });
        closeFunc();
      }
    }
  });

  const publishFunc = (params: any) => {
    publishDashboardItem({
      game_code: gameCode,
      dashboard_custom_id: publishId,
      u_parent_id: params?.parentId,
      level: params?.selectedLevel,
      name_en: params?.nameEN || '',
      name_cn: params?.nameCN || '',
      publish_status: 1,
    });
  };
  // 点击confirm 按钮
  const handleClickConfirm = () => {
    const parentId = selectSubFolder ? selectSubFolder : selectFolder;
    publishFunc({ selectedLevel, nameEN, nameCN, parentId });
  };

  // select增加搜索后点击就会触发失去焦点导致提前校验，增加状态控制
  useEffect(() => {
    if(!hasClickedLevel)return;
    checkUserLevel();
  }, [selectedLevel]);

  return (
    <Modal
      className="dbl-publish-modal"
      visible={true}
      size={500}
      caption={formatMessage({ id: 'dbl_common_publish' })}
      onClose={closeFunc}
    >
      <Modal.Body>
        <Form>
          <Form.Item
            required
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_common_dashboard_title' }) + formatMessage({ id: 'dbl_common_name' })}
            status={nameENErr ? 'error' : undefined}
            message={nameENErr}>
            <Input style={{ width: '100%' }} value={nameEN} onChange={(val) => setNameEN(val) } onBlur={checkNameEN} placeholder={formatMessage({ id: 'dbl_menu_enter_dashboard_name' })} />
          </Form.Item>
          <Form.Item
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_menu_chinese' }) + formatMessage({ id: 'dbl_common_name' })}
            status={nameCNErr ? 'error' : undefined}
            message={nameCNErr}>
            <Input style={{ width: '100%' }} value={nameCN} onChange={(val) => { setNameCN(val) }} onBlur={checkNameCN} placeholder={formatMessage({ id: 'dbl_menuenter_dashboard_ch_name' })} />
          </Form.Item>
          <Form.Item required label={formatMessage({ id: 'dbl_menu_menu' })} showStatusIcon={false}
            status={menuErr ? 'error' : undefined}
            message={menuErr}
          >
            <Select
              appearance="button"
              options={formatMenuList}
              value={selectMenu}
              size="full"
              matchButtonWidth
              onChange={value => setSelectMenu(value)}
              onBlur={checkMenu}
              placeholder={commonFormatMessage('select_placeholder')}
              tips={
                (menuLoading ||menuError || formatMenuList?.length === 0) && <Abnormal
                  style={{ height: '20px' }}
                  isLoading={menuLoading}
                  isError={menuError}
                  isNoData={formatMenuList?.length === 0}
                  nodataMsg={commonFormatMessage('select_no_data')}
                />
              }
            />
          </Form.Item>
          <Form.Item
            showStatusIcon={false}
            required={true}
            label={formatMessage({ id: 'dbl_menu_folder' })}
            status={folderErr ? 'error' : undefined}
            message={folderErr}>
            <Select
              size='full'
              matchButtonWidth={true}
              appearance="button"
              options={formatFolderList}
              value={selectFolder}
              onChange={value => setSelectFolder(value)}
              onBlur={checkFolder}
              placeholder={commonFormatMessage('select_placeholder' )}
              tips={
                (menuLoading ||menuError || formatFolderList?.length === 0)  && <Abnormal
                  style={{ height: '20px' }}
                  isLoading={menuLoading}
                  isError={menuError}
                  isNoData={formatFolderList?.length === 0}
                  nodataMsg={commonFormatMessage('select_no_data')}
                />
              }
            />
          </Form.Item>
          <Form.Item
            showStatusIcon={false}
            required={false}
            label={formatMessage({ id: 'dbl_menu_subfolder' })}
            status={subFolderErr ? 'error' : undefined}
            message={subFolderErr}>
            <Select
              size='full'
              matchButtonWidth={true}
              appearance="button"
              options={formatSubFolderList}
              value={selectSubFolder}
              onChange={value => setSelectSubFolder(value)}
              // onBlur={checkSubFolder}
              placeholder={commonFormatMessage('select_placeholder' )}
              tips={
                (menuLoading ||menuError || formatSubFolderList?.length === 0) && <Abnormal
                  style={{ height: '20px' }}
                  isLoading={menuLoading}
                  isError={menuError}
                  isNoData={formatSubFolderList?.length === 0}
                  nodataMsg={commonFormatMessage('select_no_data')}
                />
              }
            />
          </Form.Item>
          <Form.Item
            className='dbl-dialog-level'
            showStatusIcon={false}
            required={true}
            label={formatMessage({ id: 'dbl_menu_menu_level' })}
            status={userLevelErr ? 'error' : undefined}
            message={userLevelErr}>      
            <Bubble
              dark={true}
              placement='right'
              trigger='click'
              content={userLevelList.length ? <div className="dbl-level-bubble-content" style={{ maxHeight: '300px', overflowY: 'auto' }}>{userLevelList.map(item => <p key={item.value}>·{item.text}({item.des})</p>)}</div> : ''}>
              <Select
                size='full'
                searchable
                matchButtonWidth={true}
                appearance="button"
                options={userLevelList}
                value={selectedLevel}
                onChange={value => setSelectedLevel(value)}
                onFocus={()=>{
                  setHasClickedLevel(true);
                }}
                onBlur={(event) =>{
                  const relatedTarget = event.relatedTarget;
                  if (relatedTarget?.className?.includes('tea-input--search')) {
                    // 如果焦点移动到搜索输入框，不执行失去焦点的逻辑
                    return levelError;
                  }
                  checkUserLevel();
                }}
                onClose={() => {
                  checkUserLevel();
                }}
                placeholder={commonFormatMessage('select_placeholder' )}
                tips={(selectedOptions) => {
                  return (levelLoading || levelError || selectedOptions?.length === 0) && <Abnormal
                    style={{ height: '20px' }}
                    isLoading={levelLoading}
                    isError={levelError}
                    isNoData={selectedOptions?.length === 0}
                    nodataMsg={commonFormatMessage('select_no_data')}
                  />;
                }
                }
              />
            </Bubble>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" disabled={isNull || Boolean(isError)}
          onClick={handleClickConfirm}>
          {formatMessage({ id: 'dbl_common_save' })}
        </Button>
        <Button type="weak" onClick={closeFunc}>
          {formatMessage({ id: 'dbl_common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PublishModal;

